import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import ContactsCache, {
  type ContactCustomPropertyInfo,
  type ContactCustomPropertyInfoSettings,
  type ContactCustomPropertyType,
  PropertyTypeLabels,
} from 'donor-elf-web/services/contacts-cache';
import Settings from 'donor-elf-web/services/settings';
import validateContactCustomProperty from 'donor-elf-web/validations/contact-custom-property';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import type { CustomPropertyEditListOptionsSaveHandler } from '../../details/properties/custom-property/edit-list-options';

export interface ContactSaveNewPropertyArgs {
  l: string;
  v: any;
  t: ContactCustomPropertyType;
  o?: string[];
  s?: ContactCustomPropertyInfoSettings;
}

interface ComponentArgs {
  onSave: (saveArgs: ContactSaveNewPropertyArgs) => Promise<void>; // this method must take care of saving the new property to the server
  onCancel: () => void;
}

export default class ContactDetailsPropertiesCustomPropertyAddNewComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    let validation = validateContactCustomProperty({
      contactsCache: this.contactsCache,
      exclude: ['t', 'v'],
      isNewProperty: true,
    });
    this.changeset = new Changeset(
      { l: undefined },
      lookupValidator(validation),
      validation,
    );
  }

  @service declare contactsCache: ContactsCache;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked changeset?: any;
  @tracked propertyInfo?: ContactCustomPropertyInfo;
  @tracked stage?: { label?: true; type?: true; value?: true } = {
    label: true,
  };
  @tracked showLabelValidationErrors = false;
  @tracked showTypeValidationErrors = false;
  @tracked showValueValidationErrors = false;
  @tracked addListOptions = false;
  @tracked saving = false;
  @tracked saveError?: any;

  get validLabelChosen() {
    return isPresent(this.changeset.get('l')) && this.changeset.error.l == null;
  }

  get validTypeChosen() {
    return isPresent(this.changeset.get('t')) && this.changeset.error.t == null;
  }

  get selectedType() {
    return this.changeset.get('t');
  }

  get selectedTypeLabel() {
    return (PropertyTypeLabels as any)[this.selectedType];
  }

  get selectedTypeIsList() {
    return this.changeset.get('t') === 'l';
  }

  get propertyTypeSelectOptions(): SelectOption[] {
    return [
      {
        val: 's',
        label: PropertyTypeLabels.s,
      },
      {
        val: 'n',
        label: PropertyTypeLabels.n,
      },
      {
        val: 'b',
        label: PropertyTypeLabels.b,
      },
      {
        val: 'c',
        label: PropertyTypeLabels.c,
      },
      {
        val: 'l',
        label: PropertyTypeLabels.l,
      },
    ];
  }

  @action
  async handleLabelNext() {
    this.showLabelValidationErrors = true;
    await this.changeset.validate();
    if (this.validLabelChosen) {
      let validation = validateContactCustomProperty({
        contactsCache: this.contactsCache,
        exclude: ['v'],
        isNewProperty: true,
      });
      this.changeset = new Changeset(
        { l: this.changeset.get('l'), t: undefined },
        lookupValidator(validation),
        validation,
      );
      this.stage = { type: true };
    }
  }

  @action
  async handleLabelTabbedOrEntered(e: any) {
    await this.handleLabelNext();
    if (this.validLabelChosen) {
      e.target.blur();
    }
  }

  @action
  async handleTypeChange() {
    this.showTypeValidationErrors = true;
    await this.changeset.validate();
    if (this.validTypeChosen) {
      let validation = validateContactCustomProperty({
        contactsCache: this.contactsCache,
        isNewProperty: true,
      });
      this.changeset = new Changeset(
        {
          l: this.changeset.get('l'),
          t: this.changeset.get('t'),
          v: undefined,
        },
        lookupValidator(validation),
        validation,
      );
      this.stage = { value: true };
      this.propertyInfo = {
        l: this.changeset.get('l'),
        t: this.changeset.get('t'),
      };
      if (this.selectedTypeIsList) {
        this.addListOptions = true;
      }
    }
  }

  @action
  async handleValuePlaceholderClicked() {
    this.showLabelValidationErrors = true;
    this.showTypeValidationErrors = true;
    await this.changeset.validate();
  }

  @action
  handleEditListOptionsSave(args: CustomPropertyEditListOptionsSaveHandler) {
    this.propertyInfo = {
      l: this.changeset.get('l'),
      t: this.changeset.get('t'),
      o: args.options,
      s: args.settings,
    };
    // if single selection, then automatically select first option
    if (args.settings?.single_selection && args.options.length > 0) {
      let val: any = {};
      val[args.options[0]] = true;
      this.changeset.set('v', val);
    }
    this.addListOptions = false;
  }

  @action
  async save() {
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      this.saving = true;
      try {
        await this.args.onSave({
          l: this.changeset.get('l'),
          v: this.changeset.get('v'),
          t: this.changeset.get('t'),
          o: this.changeset.get('o'),
          s: this.propertyInfo?.s,
        });
      } catch (e) {
        this.saveError = e;
      }
      this.saving = false;
    } else {
      this.showValueValidationErrors = true;
    }
  }
}
